import { Box, Button, Card, Stack, TextField, Typography } from '@mui/material'
import { IconSendMessage } from '../../../icons/IconSendMessage'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import type { KnownNotificationResource } from '../../../api/notifications'
import { useMarkNotificationsReadMutation } from '../../../api/notifications'
import {
  useAcceptTeamMemberInvitationMutation,
  useDeclineTeamMemberInvitationMutation,
} from '../../../api/teams'
import { mediaSourceUrl } from './utils'
import modalEmitter from '../../ModalComponent/modalEmitter'
import { toast } from 'react-toastify'
import { IconTeams } from '../../../icons/IconTeams'
import { NotificationEntityHeader } from './NotificationEntityHeader'
import { createNotificationNavHandlers, getTeamSlug } from './notificationNav'
import { ProjectNameQuoted } from './notificationTypography'
import { NotificationTypeChip } from './NotificationTypeChip'
import { NotificationExecutedLine } from './NotificationExecutedLine'
import { notificationBodyWrapSx, notificationMessageClampSx } from './notificationTextClamp'
import { COLORS } from '../../../styles/variables'
import { extractApiErrorMessage } from '../../../utils/api'

type Props = {
  notification: Extract<KnownNotificationResource, { type: 'team.invitation.created' }>
}

type DecisionAction = 'accept' | 'decline'

function teamTitleFromBlock(team: { title?: string; name?: string } | null | undefined): string {
  const title = typeof team?.title === 'string' ? team.title.trim() : ''
  if (title.length > 0) return title
  const name = typeof team?.name === 'string' ? team.name.trim() : ''
  return name.length > 0 ? name : 'команда'
}

function ResponseMessageModalBody(props: {
  action: DecisionAction
  invitationId: number
  notificationId: number
  onResolved: (nextStatus: 'accepted' | 'declined') => void
}) {
  const { action, invitationId, notificationId, onResolved } = props
  const [message, setMessage] = React.useState('')
  const [accept, acceptState] = useAcceptTeamMemberInvitationMutation()
  const [decline, declineState] = useDeclineTeamMemberInvitationMutation()
  const [markRead] = useMarkNotificationsReadMutation()

  const isLoading = acceptState.isLoading || declineState.isLoading

  const handleConfirm = async () => {
    const response_message = message.trim()
    try {
      if (action === 'accept') {
        await accept({
          invitationId,
          ...(response_message.length > 0 ? { response_message } : {}),
        }).unwrap()
        toast.success('Приглашение принято')
        onResolved('accepted')
      } else {
        await decline({
          invitationId,
          ...(response_message.length > 0 ? { response_message } : {}),
        }).unwrap()
        toast.success('Приглашение отклонено')
        onResolved('declined')
      }
    } catch (error) {
      toast.error(extractApiErrorMessage(error) ?? 'Не удалось выполнить действие')
      return
    }

    try {
      await markRead([notificationId]).unwrap()
    } catch {
      // Не блокируем UX: основное действие уже выполнено.
    }

    modalEmitter.emitCloseModal()
  }

  return (
    <Stack gap={2}>
      <Typography variant="black-14-light">Сообщение (необязательно)</Typography>
      <TextField
        placeholder="Напишите причину или комментарий"
        multiline
        minRows={4}
        value={message}
        onChange={(e) => setMessage(e.target.value.slice(0, 500))}
        helperText={`${message.length}/500`}
        disabled={isLoading}
        fullWidth
      />
      <Stack direction="row" gap={2} justifyContent="flex-end">
        <Button variant="follow" disabled={isLoading} onClick={() => modalEmitter.emitCloseModal()}>
          Отмена
        </Button>
        <Button
          variant={action === 'accept' ? 'action' : 'danger'}
          disabled={isLoading}
          onClick={handleConfirm}
        >
          Подтвердить
        </Button>
      </Stack>
    </Stack>
  )
}

export function TeamCatalogInvitationCreatedNotificationCard({ notification }: Props) {
  const navigate = useNavigate()
  const { onTeamClick } = createNotificationNavHandlers(navigate)
  const [optimisticStatus, setOptimisticStatus] = React.useState<'accepted' | 'declined' | null>(null)
  const payload = notification.payload
  const team = payload?.team
  const actor = payload?.actor
  const invitation = payload?.invitation

  const teamTitle = teamTitleFromBlock(team)
  const actorName =
    typeof actor?.fullname === 'string' && actor.fullname.trim().length > 0 ? actor.fullname : 'Пользователь'

  const invitationId = Number(invitation?.id)
  const invitationStatusRaw = typeof invitation?.status === 'string' ? invitation.status : ''
  const invitationStatus = optimisticStatus ?? invitationStatusRaw
  const canAct =
    invitationStatus === 'pending' && Number.isFinite(invitationId) && invitationId > 0

  const showExecutedLine = !canAct && Boolean(invitationStatus) && invitationStatus !== 'pending'

  const openDecisionModal = (action: DecisionAction) => {
    if (!canAct) return
    modalEmitter.emitOpenModal({
      dialog: {
        title: action === 'accept' ? 'Принять приглашение' : 'Отклонить приглашение',
        body: (
          <ResponseMessageModalBody
            action={action}
            invitationId={invitationId}
            notificationId={notification.id}
            onResolved={(nextStatus) => setOptimisticStatus(nextStatus)}
          />
        ),
        modalToCentrPosition: true,
      },
    })
  }

  const teamAvatarSrc = mediaSourceUrl(team?.image_url) ?? undefined
  const teamSlug = getTeamSlug(team)

  return (
    <Card variant="primaryAdaptiveCard" sx={{ p: 2 }}>
      <Stack direction="column" gap={1.25} minWidth={0}>
        <Box
          onClick={(e) => onTeamClick(e, teamSlug)}
          sx={{ cursor: teamSlug ? 'pointer' : 'default', alignSelf: 'stretch' }}
        >
          <NotificationEntityHeader
            title={teamTitle}
            avatarSrc={teamAvatarSrc}
            fallbackIcon={<IconTeams fontSize="small" sx={{ color: 'inherit' }} />}
            subtitle={
              <>
                <Typography variant="black-12-light" component="span" sx={{ minWidth: 0, ...notificationBodyWrapSx }}>
                  {`от ${actorName}`}
                </Typography>
                <IconSendMessage sx={{ width: 16, height: 16, flexShrink: 0, color: COLORS.STOPLIGHT_GO_GREEN }} />
                <NotificationTypeChip variant="invitation" />
              </>
            }
          />
        </Box>

        <Stack direction="column" minWidth={0} flex={1} gap={0.5}>
          <Typography variant="black-14-normal" sx={notificationBodyWrapSx}>
            Вас пригласили в <ProjectNameQuoted name={teamTitle} pathKey={teamSlug} onNameClick={onTeamClick} />
          </Typography>
          {typeof invitation?.message === 'string' && invitation.message.trim().length > 0 && (
            <Typography variant="black-12-light" sx={notificationMessageClampSx}>
              {invitation.message}
            </Typography>
          )}
          <Typography variant="black-12-light">Вы можете принять или отказаться</Typography>

          {canAct && (
            <Stack direction="row" gap={1} sx={{ pt: 1 }}>
              <Button
                variant="action"
                onClick={(e) => {
                  e.stopPropagation()
                  openDecisionModal('accept')
                }}
              >
                Принять приглашение
              </Button>
              <Button
                variant="danger"
                onClick={(e) => {
                  e.stopPropagation()
                  openDecisionModal('decline')
                }}
              >
                Отказаться
              </Button>
            </Stack>
          )}

          {showExecutedLine && <NotificationExecutedLine status={invitationStatus} />}

          <Typography variant="black-12-light" noWrap sx={{ opacity: 0.8 }}>
            {notification.read_at ? 'Прочитано' : 'Непрочитано'} • {new Date(notification.created_at).toLocaleString()}
          </Typography>
        </Stack>
      </Stack>
    </Card>
  )
}
